import React, { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'

import { useTranslations } from 'next-intl'

import {
  Text,
  Link,
  Box,
  Button,
  Modal,
  ModalHeader,
  ModalOverlay,
  ModalBody,
  ModalContent,
  ModalCloseButton,
  ModalFooter,
  VStack,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  useColorModeValue,
  HStack,
} from '@chakra-ui/react'

import {
  getUserMiningPools,
  updateMiningPoolNickname,
  updateMiningPoolUsername,
  updateMiningPoolWatcherUri,
  updateMiningPoolStratums,
  applySpiderPoolFeeRateMutation,
} from '@/api/miningPool'

import { IMiningPoolNode } from '@/types/miningPool.interface'

import {
  FieldErrors,
  RegisterOptions,
  UseFormRegisterReturn,
  useForm,
} from 'react-hook-form'
import { RightArrowIcon } from '@/components/icons/RightArrowIcon'
import {
  SUPPORT_MINING_POOL_CONFIGURATION_URL,
  SUPPORT_SPIDER_POOL_CONFIGURATION_URL,
  SUPPORT_URL,
} from '@/constants'
import { NOT_XSS_PATTERN } from '@/utils/inputPattern'

type MiningPoolFormValues = {
  miningPoolName: string
  username: string
  watcherLink: string
  poolNickname: string
  miningPoolAgreement: boolean
  defaultMiningPool: boolean
  miningPoolStratum1: string
  miningPoolStratum2: string
  miningPoolStratum3: string
}

type EditPoolAccountModalProps = {
  isOpen: boolean
  onClose: () => void
  userMiningPoolItem: IMiningPoolNode | undefined
}

const EditPoolAccountModal: React.FC<EditPoolAccountModalProps> = ({
  isOpen,
  onClose,
  userMiningPoolItem,
}: EditPoolAccountModalProps) => {
  const blue50 = useColorModeValue('blue.50', 'blue.900')

  const gray700 = useColorModeValue('gray.700', 'gray.200')
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [spiderUsernameError, setSpiderUsernameError] = useState<string | null>(
    null
  )
  const [spiderFeeLoading, setSpiderFeeLoading] = useState(false)
  const t = useTranslations()
  const toast = useToast()

  const isSpiderPool =
    userMiningPoolItem?.miningPoolName?.toLowerCase() === 'spider pool'

  const [applySpiderPoolFeeRate] = useMutation(applySpiderPoolFeeRateMutation)

  const [
    updateMiningPoolNicknameMutation,
    { loading: updateMiningPoolNicknameLoading },
  ] = useMutation(updateMiningPoolNickname, {
    onCompleted(data) {
      if (data?.updateMiningPoolNickname?.boolean) {
        toast({
          title: t('common.success'),
          description: t(
            'dashboard.mining-pool.update-pool-nickname-toast-success-description'
          ),
          status: 'success',
        })
        onClose()
      }
      if (data?.updateMiningPoolNickname === null) {
        setMutationError(t('dashboard.mining-pool.uusername-error'))
      }
      reset()
    },

    refetchQueries: [
      {
        query: getUserMiningPools,
      },
    ],
  })

  const [
    updateMiningPoolUsernameMutation,
    { loading: updateMiningPoolUsernameLoading },
  ] = useMutation(updateMiningPoolUsername, {
    refetchQueries: [
      {
        query: getUserMiningPools,
      },
    ],
  })

  const [
    updateMiningPoolWatcherUriMutation,
    { loading: updateMiningPoolWatcherUriLoading },
  ] = useMutation(updateMiningPoolWatcherUri, {
    onCompleted(data) {
      if (
        data?.updateMiningPoolWatcherUri?.boolean &&
        getValues().watcherLink !== ''
      ) {
        toast({
          title: t('common.success'),
          description: t(
            'dashboard.mining-pool.update-pool-watcher-toast-success-description'
          ),
          status: 'success',
        })
        onClose()
      }
      reset()
    },

    refetchQueries: [
      {
        query: getUserMiningPools,
      },
    ],
  })

  const [
    updateMiningPoolStratumsMutation,
    { loading: updateMiningPoolStratumsLoading },
  ] = useMutation(updateMiningPoolStratums, {
    onCompleted(data) {
      if (data?.updateMiningPoolStratums?.boolean) {
        toast({
          title: t('common.success'),
          description: t(
            'dashboard.mining-pool.update-stratum-toast-success-description'
          ),
          status: 'success',
        })
        onClose()
      }
      if (data?.updateMiningPoolStratums === null) {
        setMutationError(t('dashboard.mining-pool.update-stratum-error'))
      }
      reset()
    },

    refetchQueries: [
      {
        query: getUserMiningPools,
      },
    ],
  })

  const isLoading: boolean =
    updateMiningPoolNicknameLoading ||
    updateMiningPoolUsernameLoading ||
    updateMiningPoolWatcherUriLoading ||
    updateMiningPoolStratumsLoading ||
    spiderFeeLoading

  const preloadedDefaultValues = {
    miningPoolStratum1: userMiningPoolItem?.miningPoolStratum1,
    miningPoolStratum2: userMiningPoolItem?.miningPoolStratum2,
    miningPoolStratum3: userMiningPoolItem?.miningPoolStratum3,
    username: userMiningPoolItem?.miningPoolAccountUserName,
    poolNickname: userMiningPoolItem?.miningPoolNickName,
    watcherLink: userMiningPoolItem?.miningPoolAccountWatcherUri,
  }
  useEffect(() => {
    reset(preloadedDefaultValues)
    setSpiderUsernameError(null)
    setMutationError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm<MiningPoolFormValues>({
    defaultValues: preloadedDefaultValues,
  })
  const gray800 = useColorModeValue('gray.800', 'gray.200')

  async function onSubmit(values: MiningPoolFormValues) {
    setSpiderUsernameError(null)
    setMutationError(null)

    if (values.poolNickname !== userMiningPoolItem?.miningPoolNickName) {
      await updateMiningPoolNicknameMutation({
        variables: {
          miningPoolAccountId: userMiningPoolItem?.miningPoolAccountId,
          poolNickname: values.poolNickname,
        },
      })
    }

    if (values.username !== userMiningPoolItem?.miningPoolAccountUserName) {
      // Check if the username begins with prefix 'compass' for Spider Pool accounts
      const usernameHasCompassPrefix = values.username
        .toLowerCase()
        .startsWith('compass')

      if (isSpiderPool && usernameHasCompassPrefix) {
        setSpiderFeeLoading(true)
        try {
          const feeResult = await applySpiderPoolFeeRate({
            variables: {
              miningPoolAccountId: userMiningPoolItem?.miningPoolAccountId,
              newUsername: values.username,
            },
            errorPolicy: 'all',
          })

          const resultJson =
            feeResult?.data?.applySpiderPoolFeeRate?.spiderPoolFeeRateResult

          if (resultJson?.success != true) {
            setSpiderFeeLoading(false)
            setSpiderUsernameError(
              resultJson?.errorMessage ||
                'Spider Pool could not verify this username. Please make sure the account exists on Spider Pool and try again.'
            )
            return
          }
        } catch {
          setSpiderFeeLoading(false)
          setSpiderUsernameError(
            'Failed to validate Spider Pool username. Please try again.'
          )
          return
        }
        setSpiderFeeLoading(false)
      }
      await updateMiningPoolUsernameMutation({
        variables: {
          miningPoolAccountId: userMiningPoolItem?.miningPoolAccountId,
          poolUsername: values.username,
        },
      })

      toast({
        title: t('common.success'),
        description: t(
          'dashboard.mining-pool.update-pool-username-toast-success-description'
        ),
        status: 'success',
      })
      onClose()
      reset()
    }

    if (
      values.watcherLink !== userMiningPoolItem?.miningPoolAccountWatcherUri
    ) {
      await updateMiningPoolWatcherUriMutation({
        variables: {
          miningPoolAccountId: userMiningPoolItem?.miningPoolAccountId,
          watcherUri: values.watcherLink,
        },
      })
    }
    if (values.miningPoolStratum1 !== userMiningPoolItem?.miningPoolStratum1) {
      await updateMiningPoolStratumsMutation({
        variables: {
          miningPoolAccountId: userMiningPoolItem?.miningPoolAccountId,
          poolStratum1: values?.miningPoolStratum1,
        },
      })
    }
    if (values.miningPoolStratum2 !== userMiningPoolItem?.miningPoolStratum2) {
      await updateMiningPoolStratumsMutation({
        variables: {
          miningPoolAccountId: userMiningPoolItem?.miningPoolAccountId,
          poolStratum2: values?.miningPoolStratum2,
        },
      })
    }
    if (values.miningPoolStratum3 !== userMiningPoolItem?.miningPoolStratum3) {
      await updateMiningPoolStratumsMutation({
        variables: {
          miningPoolAccountId: userMiningPoolItem?.miningPoolAccountId,
          poolStratum3: values?.miningPoolStratum3,
        },
      })
    }
  }
  return (
    <>
      <Modal
        isCentered
        onClose={() => (
          onClose(),
          reset(),
          setMutationError(null),
          setSpiderUsernameError(null)
        )}
        isOpen={isOpen}
        motionPreset="slideInBottom"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent maxW="626px">
          <ModalHeader
            alignItems={'center'}
            justifyContent={'center'}
            display={'flex'}
            pt="6"
            pb="1"
          >
            {t('dashboard.mining-pool.edit-pool-account')}
          </ModalHeader>
          <Text
            fontSize="sm"
            color="gray.500"
            justifyContent={'center'}
            align={'center'}
          >
            {t('dashboard.mining-pool.add-pool-account-description')}
          </Text>
          <ModalCloseButton />
          <ModalBody pt="5">
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={2}>
                <EditOtherPoolMiningStratums
                  userMiningPoolItem={userMiningPoolItem}
                  register={register}
                  errors={errors}
                  gray800={gray800}
                  SUPPORT_MINING_POOL_CONFIGURATION_URL={
                    SUPPORT_MINING_POOL_CONFIGURATION_URL
                  }
                />
                <FormControl id="username" isInvalid={!!errors?.username}>
                  <FormLabel fontSize="sm" color={gray800} fontWeight={600}>
                    {t('dashboard.mining-pool.username')}
                    <Text as="span" color="orange.500">
                      *
                    </Text>
                  </FormLabel>
                  <Text
                    fontSize="10px"
                    color={gray800}
                    width={'full'}
                    mt="-2"
                    mb="2"
                  >
                    {t('dashboard.mining-pool.username-description')}{' '}
                    <Link
                      href={SUPPORT_MINING_POOL_CONFIGURATION_URL}
                      isExternal
                      display="inline"
                      target="_blank"
                    >
                      {t('home.read-more')}
                    </Link>
                  </Text>
                  <Input
                    type="text"
                    placeholder={userMiningPoolItem?.miningPoolAccountUserName}
                    {...register('username', {
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        if (spiderUsernameError) setSpiderUsernameError(null)
                        const value = e.target.value
                        const formattedValue = isSpiderPool
                          ? value.toLowerCase()
                          : value
                        setValue('username', formattedValue, {
                          shouldValidate: true,
                        })
                      },
                    })}
                  />
                  {isSpiderPool && (
                    <>
                      <Box
                        bg={blue50}
                        p="0 8px"
                        justifyContent="center"
                        rounded={'sm'}
                        mt="2"
                      >
                        <HStack align="center" spacing="2">
                          <Text color={gray700} fontSize="xs" fontWeight={400}>
                            {t(
                              'dashboard.mining-pool.spider-pool-username-prefix'
                            )}
                          </Text>
                          <Link
                            href={SUPPORT_SPIDER_POOL_CONFIGURATION_URL}
                            isExternal
                            textDecoration="underline"
                            color="blue.500"
                            fontSize="xs"
                            fontWeight={600}
                            _hover={{ color: 'blue.600' }}
                          >
                            <Text>
                              {t(
                                'dashboard.mining-pool.tooltip-username-learn-more'
                              )}
                            </Text>
                          </Link>
                        </HStack>
                      </Box>
                    </>
                  )}
                  <FormErrorMessage>
                    {errors.username && errors.username.message}
                  </FormErrorMessage>
                  {isSpiderPool && spiderUsernameError && (
                    <Text fontSize="sm" color="red.500" mt="1">
                      {spiderUsernameError}
                    </Text>
                  )}
                </FormControl>
                <FormControl id="watcherLink" isInvalid={!!errors?.watcherLink}>
                  <FormLabel fontSize="sm" color={gray800} fontWeight={600}>
                    {t('dashboard.mining-pool.watcher-link')}
                    <Text as="span" color="gray.400" mx="1">
                      ({t('contact-sales.form.optional')})
                    </Text>

                    <Text
                      fontSize="10"
                      color="gray.500"
                      justifyContent={'start'}
                      display={'flex'}
                      align={'left'}
                      width={'full'}
                    >
                      {t('dashboard.mining-pool.watcher-link-description')}
                    </Text>
                  </FormLabel>
                  <Input
                    type="text"
                    placeholder={
                      userMiningPoolItem?.miningPoolAccountWatcherUri ||
                      t('dashboard.mining-pool.watcher-link')
                    }
                    {...register('watcherLink', {
                      pattern: {
                        value: NOT_XSS_PATTERN,
                        message: t(
                          'dashboard.settings.user-account-settings.errors.invalid-pattern',
                          { field: t('dashboard.mining-pool.watcher-link') }
                        ),
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {errors.watcherLink && errors.watcherLink.message}
                  </FormErrorMessage>
                </FormControl>
              </VStack>

              <VStack spacing={3}>
                <Box w="100%" display="flex" justifyContent="center">
                  <Button
                    type="submit"
                    variant="brand"
                    colorScheme="brand"
                    w="75%"
                    mt="4"
                    justifyContent={'center'}
                    isLoading={isLoading}
                  >
                    {t('common.save-changes')}
                  </Button>
                </Box>
                {mutationError && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>{mutationError}</AlertDescription>
                  </Alert>
                )}
              </VStack>
            </form>
          </ModalBody>
          <ModalFooter w="full" justifyContent={'center'} pb="6" pt="2">
            <Text fontSize={'sm'} color={'gray.500'}>
              {t('dashboard.mining-pool.need-help-setting-up')}
              <Link href={`${SUPPORT_URL}/articles/4446509937821`}>
                <Text
                  as="span"
                  color={'brand.600'}
                  mx="2"
                  textDecoration={'underline'}
                  _hover={{
                    color: 'brand.500',
                  }}
                >
                  {t('dashboard.mining-pool.view-our-guide')}
                </Text>
                <RightArrowIcon />
              </Link>
            </Text>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

type EditOtherPoolMiningStratumsProps = {
  userMiningPoolItem: IMiningPoolNode | undefined
  register: (
    name:
      | 'miningPoolName'
      | 'username'
      | 'watcherLink'
      | 'poolNickname'
      | 'miningPoolAgreement'
      | 'defaultMiningPool'
      | 'miningPoolStratum1'
      | 'miningPoolStratum2'
      | 'miningPoolStratum3',
    options?: RegisterOptions<MiningPoolFormValues> | undefined
  ) => UseFormRegisterReturn<string>
  errors: FieldErrors<MiningPoolFormValues>
  gray800: string
  SUPPORT_MINING_POOL_CONFIGURATION_URL: string
}

const EditOtherPoolMiningStratums = ({
  userMiningPoolItem,
  register,
  errors,
  gray800,
  SUPPORT_MINING_POOL_CONFIGURATION_URL,
}: EditOtherPoolMiningStratumsProps) => {
  const t = useTranslations()
  return (
    <>
      {userMiningPoolItem?.miningPoolName === 'Other Pool' && (
        <>
          {/* Mining Pool Stratum 1 */}
          <FormControl
            id="miningPoolStratum"
            isInvalid={!!errors?.miningPoolStratum1}
            placeholder={userMiningPoolItem?.miningPoolStratum1}
          >
            <FormLabel fontSize="sm" color={gray800}>
              {t('dashboard.mining-pool.mining-pool-stratum')}
              <Text as="span" color="orange.500">
                *
              </Text>
              <Text
                fontSize="10"
                color={gray800}
                justifyContent={'start'}
                display={'flex'}
                align={'left'}
                width={'full'}
              >
                {t('dashboard.mining-pool.mining-pool-stratum-description')}
                <Link href={SUPPORT_MINING_POOL_CONFIGURATION_URL} isExternal>
                  <Text as="span" mx="2">
                    {t('home.read-more')}
                  </Text>
                </Link>
              </Text>
            </FormLabel>
            <Input type="text" {...register('miningPoolStratum1')} />
            <FormErrorMessage>
              {errors.miningPoolStratum1 && errors.miningPoolStratum1.message}
            </FormErrorMessage>
          </FormControl>

          {/* Mining Pool Backup Stratum 2 */}
          <FormControl
            id="miningPoolStratum2"
            isInvalid={!!errors?.miningPoolStratum2}
          >
            <FormLabel fontSize="sm" color={gray800}>
              {t('dashboard.mining-pool.backup-stratum')}
              <Text as="span" color="gray.400" mx="1">
                ({t('contact-sales.form.optional')})
              </Text>
            </FormLabel>
            <Input
              placeholder={userMiningPoolItem?.miningPoolStratum2}
              type="text"
              {...register('miningPoolStratum2')}
            />
            <FormErrorMessage>
              {errors.miningPoolStratum2 && errors.miningPoolStratum2.message}
            </FormErrorMessage>
          </FormControl>

          {/* Mining Pool Backup Stratum 3 */}
          <FormControl
            id="miningPoolStratum3"
            isInvalid={!!errors?.miningPoolStratum3}
          >
            <FormLabel fontSize="sm" color={gray800}>
              {t('dashboard.mining-pool.backup-stratum')}
              <Text as="span" color="gray.400" mx="1">
                ({t('contact-sales.form.optional')})
              </Text>
            </FormLabel>
            <Input
              placeholder={userMiningPoolItem?.miningPoolStratum3}
              type="text"
              {...register('miningPoolStratum3')}
            />
            <FormErrorMessage>
              {errors.miningPoolStratum3 && errors.miningPoolStratum3.message}
            </FormErrorMessage>
          </FormControl>
        </>
      )}
    </>
  )
}

export default EditPoolAccountModal
